import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';

import { useEditorNavigator } from 'src/editor/use-editor-navigator.hook';
import { PlayerContentAnnotationTooltip } from 'src/player/content-annotation-tooltip';
import { PlayerNavigator } from 'src/player/navigator';
import { useEditorContext } from 'src/store/editor';
import { useStepEditorContext } from 'src/store/step-editor';
import { getNextNode, getPreviousNode } from 'src/utils/journey';
import { EventBus } from 'src/common/EventBus';
import { Creator } from 'src/common/interfaces/creator.interface';
import { StepEditorContentConfigureStageTypeEnum } from '../content/configure/stage-type.enum';
import { useCurrentOrganization } from 'src/store/organization';
import { useCurrentUser } from 'src/store/user';

export const StepEditorPreviewNavigator = () => {
  const { currentConfigurationStage } = useStepEditorContext();
  const { journey, setCurrentActiveNodeId } = useEditorContext();
  const annotation = useStepEditorContext((state) => state.annotation);
  const { node_id: nodeId, setStateFromNode, creator } = useStepEditorContext();
  const { navigateToContentEdit } = useEditorNavigator();
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const currentUser = useCurrentUser((state) => state.currentUser);

  const previousStepAndNode = useMemo(() => {
    if (!journey || !nodeId) return [null, null];

    const [step, previousNode] = getPreviousNode(journey, nodeId);
    return [step, previousNode];
  }, [nodeId, journey]);

  const nextStepAndNode = useMemo(() => {
    if (!journey || !nodeId) return [null, null];

    const [step, nextNode] = getNextNode(journey, nodeId);
    return [step, nextNode];
  }, [nodeId, journey]);


  const goToPreviousNode = useCallback(() => {
    // go to previous node
    const [step, previousNode] = previousStepAndNode;
    if (step && previousNode) {
      if (currentConfigurationStage === StepEditorContentConfigureStageTypeEnum.BOARD) {
        setCurrentActiveNodeId(previousNode.id);
        setStateFromNode(previousNode);
      } else {
        EventBus.emit('editor:reset-stage', { showInitialStage: false });
        navigateToContentEdit(step.id, previousNode.id, {
          replaceHistory: true,
        });
      }
    }
  }, [previousStepAndNode, setCurrentActiveNodeId, setStateFromNode, navigateToContentEdit, currentConfigurationStage]);

  const goToNextNode = useCallback(() => {
    // go to next node
    const [step, nextNode] = nextStepAndNode;
    if (step && nextNode) {
      if (currentConfigurationStage === StepEditorContentConfigureStageTypeEnum.BOARD) {
        setCurrentActiveNodeId(nextNode.id);
        setStateFromNode(nextNode);
      } else {
        EventBus.emit('editor:reset-stage', { showInitialStage: false });
        navigateToContentEdit(step.id, nextNode.id, {
          replaceHistory: true,
        });
      }
    }
  }, [nextStepAndNode, currentConfigurationStage, setStateFromNode, setCurrentActiveNodeId, navigateToContentEdit]);

  if (currentOrganization.feature_flags.hide_desktop_navigation) return null;

  return (
    <div className='mt-6'>
      <div className='relative flex flex-none w-full min-h-0 flex-col space-y-4'>
        <div
          className={classNames('flex items-center space-x-2 px-6 md:px-0 md:mb-0', {
            'justify-between': nodeId,
            'justify-center': !nodeId,
          })}
        >
          <div className='flex flex-1' />
          {journey && annotation && annotation.enabled && (
            <PlayerContentAnnotationTooltip
              creator={(creator || currentUser) as Creator}
              annotation={annotation} />
          )}
          {nodeId && (
            <div className='flex flex-1'>
              <PlayerNavigator
                isFirst={previousStepAndNode[1] === null}
                isLast={nextStepAndNode[1] === null}
                onClickPrevious={goToPreviousNode}
                onClickNext={goToNextNode}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
